import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { 
  AlertTriangle, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  RefreshCw, 
  Search,
  Plus,
  Image,
  IndianRupee,
  Factory,
  Users,
  ShoppingCart,
  FileText,
  Eye,
  UserCheck,
  Calendar,
  Trash2,
  AlertOctagon,
  Edit
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  raised: { label: 'Raised', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  under_review: { label: 'Under Review', color: 'bg-purple-100 text-purple-800 border-purple-200' },
  ca_assigned: { label: 'CA/PA Assigned', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  ca_in_progress: { label: 'CA In Progress', color: 'bg-orange-100 text-orange-800 border-orange-200' },
  ca_completed: { label: 'CA Completed', color: 'bg-teal-100 text-teal-800 border-teal-200' },
  pa_assigned: { label: 'PA Assigned', color: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  pa_in_progress: { label: 'PA In Progress', color: 'bg-cyan-100 text-cyan-800 border-cyan-200' },
  pa_completed: { label: 'PA Completed', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  closed: { label: 'Closed', color: 'bg-green-100 text-green-800 border-green-200' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800 border-red-200' }
};

const severityConfig = {
  critical: { label: 'Critical', color: 'bg-red-100 text-red-700 border-red-300', icon: '🔴' },
  major: { label: 'Major', color: 'bg-orange-100 text-orange-700 border-orange-300', icon: '🟠' },
  minor: { label: 'Minor', color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: '🟡' }
};

const sourceConfig = {
  internal: { label: 'Internal (Factory)', icon: Factory },
  sales: { label: 'Sales Team', icon: ShoppingCart },
  customer: { label: 'Customer', icon: Users }
};

export default function ComplaintsPage() {
  const { user, getAuthHeader } = useAuth();
  const [complaints, setComplaints] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('all');
  
  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [capaDialogOpen, setCapaDialogOpen] = useState(false);
  const [caCompleteDialogOpen, setCaCompleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Form states
  const [newComplaint, setNewComplaint] = useState({
    source: 'internal',
    severity: 'major',
    category: 'Quality Defect',
    title: '',
    description: '',
    images: [],
    work_order_id: '',
    process_name: '',
    customer_name: ''
  });
  const [editComplaint, setEditComplaint] = useState(null);
  
  // Combined CA+PA form - description fields are optional
  const [capaForm, setCapaForm] = useState({
    ca_description: '',
    ca_assigned_to_id: '',
    ca_assigned_to_name: '',
    ca_target_date: '',
    pa_assigned_to_id: '',
    pa_assigned_to_name: '',
    pa_target_date: ''
  });
  const [caCompleteForm, setCaCompleteForm] = useState({ completion_notes: '', cost_analysis: { material_losses: [], manpower_losses: [], other_costs_description: '', other_costs_amount: 0 } });
  const [editCADialogOpen, setEditCADialogOpen] = useState(false);
  const [editPADialogOpen, setEditPADialogOpen] = useState(false);
  const [editCAForm, setEditCAForm] = useState({ completion_notes: '', cost_analysis: { material_losses: [], manpower_losses: [], other_costs_description: '', other_costs_amount: 0 } });
  const [editPAForm, setEditPAForm] = useState({ root_cause_analysis: '', preventive_action: '', completion_notes: '' });
  
  const [users, setUsers] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imagePreview, setImagePreview] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [complaintsRes, statsRes, usersRes, woRes, procRes] = await Promise.all([
        axios.get(`${API_URL}/api/complaints`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/complaints/summary/stats`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/users`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/work-orders`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/processes`, { headers: getAuthHeader() })
      ]);
      setComplaints(complaintsRes.data);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setWorkOrders(woRes.data);
      setProcesses(procRes.data);
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd/MM/yyyy');
    } catch {
      return '-';
    }
  };

  // Filter complaints
  const filteredComplaints = complaints.filter(c => {
    const matchesSearch = 
      c.complaint_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Status filter logic based on tabs
    let matchesStatus = false;
    if (activeTab === 'all') {
      matchesStatus = true;
    } else if (activeTab === 'ca_assigned') {
      matchesStatus = ['ca_assigned', 'ca_in_progress'].includes(c.status);
    } else if (activeTab === 'pa_assigned') {
      matchesStatus = ['ca_completed', 'pa_assigned', 'pa_in_progress'].includes(c.status);
    } else {
      matchesStatus = c.status === activeTab;
    }
    
    const matchesSource = sourceFilter === 'all' || c.source === sourceFilter;
    const matchesSeverity = severityFilter === 'all' || c.severity === severityFilter;
    return matchesSearch && matchesStatus && matchesSource && matchesSeverity;
  });

  const handleCreateComplaint = async () => {
    if (!newComplaint.title || !newComplaint.description) {
      toast.error('Title and description are required');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/complaints`, newComplaint, { headers: getAuthHeader() });
      toast.success('Complaint created successfully');
      setCreateDialogOpen(false);
      setNewComplaint({ source: 'internal', severity: 'major', category: 'Quality Defect', title: '', description: '', images: [], work_order_id: '', process_name: '', customer_name: '' });
      setImagePreview([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create complaint');
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (newComplaint.images.length + files.length > 5) {
      toast.error('Maximum 5 images allowed');
      return;
    }
    
    setUploadingImages(true);
    const uploadedUrls = [];
    const previewUrls = [];
    
    for (const file of files) {
      try {
        // Create preview
        previewUrls.push(URL.createObjectURL(file));
        
        // Upload to server
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(`${API_URL}/api/complaints/upload-image`, formData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
        });
        uploadedUrls.push(response.data.image_url);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setNewComplaint(prev => ({ ...prev, images: [...prev.images, ...uploadedUrls] }));
    setImagePreview(prev => [...prev, ...previewUrls]);
    setUploadingImages(false);
  };

  const removeImage = (index) => {
    setNewComplaint(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
    setImagePreview(prev => prev.filter((_, i) => i !== index));
  };

  // Check if user can edit/delete a complaint (only creator, and only if status is 'raised')
  const canEditDelete = (complaint) => {
    return complaint.raised_by_id === user?.id && complaint.status === 'raised';
  };

  // Open edit dialog
  const openEditDialog = (complaint) => {
    setEditComplaint({
      ...complaint,
      title: complaint.title || '',
      description: complaint.description || '',
      customer_name: complaint.customer_name || ''
    });
    setEditDialogOpen(true);
  };

  // Handle edit complaint
  const handleEditComplaint = async () => {
    if (!editComplaint.title || !editComplaint.description) {
      toast.error('Title and description are required');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.patch(`${API_URL}/api/complaints/${editComplaint.id}`, {
        title: editComplaint.title,
        description: editComplaint.description,
        severity: editComplaint.severity,
        customer_name: editComplaint.customer_name
      }, { headers: getAuthHeader() });
      toast.success('Complaint updated successfully');
      setEditDialogOpen(false);
      setEditComplaint(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update complaint');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete complaint
  const handleDeleteComplaint = async () => {
    setSubmitting(true);
    try {
      await axios.delete(`${API_URL}/api/complaints/${selectedComplaint.id}`, { headers: getAuthHeader() });
      toast.success('Complaint deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedComplaint(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete complaint');
    } finally {
      setSubmitting(false);
    }
  };

  // Combined CA+PA Assignment - only Assignee and Target Date are required
  const handleAssignCAPA = async () => {
    if (!capaForm.ca_assigned_to_id || !capaForm.ca_target_date || !capaForm.pa_assigned_to_id || !capaForm.pa_target_date) {
      toast.error('Please select assignees and target dates for both CA and PA');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/complaints/${selectedComplaint.id}/assign-capa`, capaForm, { headers: getAuthHeader() });
      toast.success('CA & PA assigned successfully');
      setCapaDialogOpen(false);
      setCapaForm({
        ca_description: '',
        ca_assigned_to_id: '',
        ca_assigned_to_name: '',
        ca_target_date: '',
        pa_assigned_to_id: '',
        pa_assigned_to_name: '',
        pa_target_date: ''
      });
      fetchData();
      // Refresh selected complaint
      const res = await axios.get(`${API_URL}/api/complaints/${selectedComplaint.id}`, { headers: getAuthHeader() });
      setSelectedComplaint(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign CA & PA');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteCA = async () => {
    if (!caCompleteForm.completion_notes) {
      toast.error('Completion notes are required');
      return;
    }
    
    // Calculate totals
    const materialTotal = caCompleteForm.cost_analysis.material_losses.reduce((sum, m) => sum + (m.total_cost || 0), 0);
    const manpowerTotal = caCompleteForm.cost_analysis.manpower_losses.reduce((sum, m) => sum + (m.total_cost || 0), 0);
    
    const payload = {
      completion_notes: caCompleteForm.completion_notes,
      cost_analysis: {
        ...caCompleteForm.cost_analysis,
        material_total: materialTotal,
        manpower_total: manpowerTotal,
        total_financial_impact: materialTotal + manpowerTotal + (caCompleteForm.cost_analysis.other_costs_amount || 0)
      }
    };
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/complaints/${selectedComplaint.id}/complete-ca`, payload, { headers: getAuthHeader() });
      toast.success('Corrective action completed');
      setCaCompleteDialogOpen(false);
      setCaCompleteForm({ completion_notes: '', cost_analysis: { material_losses: [], manpower_losses: [], other_costs_description: '', other_costs_amount: 0 } });
      fetchData();
      const res = await axios.get(`${API_URL}/api/complaints/${selectedComplaint.id}`, { headers: getAuthHeader() });
      setSelectedComplaint(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete CA');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseComplaint = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/complaints/${selectedComplaint.id}/close`, {}, { headers: getAuthHeader() });
      toast.success('Complaint closed');
      fetchData();
      setDetailDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close complaint');
    } finally {
      setSubmitting(false);
    }
  };

  // Add material loss item
  const addMaterialLoss = () => {
    setCaCompleteForm(prev => ({
      ...prev,
      cost_analysis: {
        ...prev.cost_analysis,
        material_losses: [...prev.cost_analysis.material_losses, { item_name: '', quantity: 0, unit: 'pcs', unit_cost: 0, total_cost: 0 }]
      }
    }));
  };

  // Add manpower loss item
  const addManpowerLoss = () => {
    setCaCompleteForm(prev => ({
      ...prev,
      cost_analysis: {
        ...prev.cost_analysis,
        manpower_losses: [...prev.cost_analysis.manpower_losses, { worker_name: '', hours: 0, hourly_rate: 0, total_cost: 0 }]
      }
    }));
  };

  // Update material loss
  const updateMaterialLoss = (index, field, value) => {
    setCaCompleteForm(prev => {
      const updated = [...prev.cost_analysis.material_losses];
      updated[index][field] = value;
      if (field === 'quantity' || field === 'unit_cost') {
        updated[index].total_cost = (updated[index].quantity || 0) * (updated[index].unit_cost || 0);
      }
      return { ...prev, cost_analysis: { ...prev.cost_analysis, material_losses: updated } };
    });
  };

  // Update manpower loss
  const updateManpowerLoss = (index, field, value) => {
    setCaCompleteForm(prev => {
      const updated = [...prev.cost_analysis.manpower_losses];
      updated[index][field] = value;
      if (field === 'hours' || field === 'hourly_rate') {
        updated[index].total_cost = (updated[index].hours || 0) * (updated[index].hourly_rate || 0);
      }
      return { ...prev, cost_analysis: { ...prev.cost_analysis, manpower_losses: updated } };
    });
  };

  // ===== EDIT CA RESPONSE =====
  const openEditCADialog = () => {
    const c = selectedComplaint;
    const ca = c.cost_analysis || { material_losses: [], manpower_losses: [], other_costs_description: '', other_costs_amount: 0 };
    setEditCAForm({
      completion_notes: c.ca_completion_notes || '',
      cost_analysis: {
        material_losses: (ca.material_losses || []).map(m => ({ ...m })),
        manpower_losses: (ca.manpower_losses || []).map(m => ({ ...m })),
        other_costs_description: ca.other_costs_description || '',
        other_costs_amount: ca.other_costs_amount || 0
      }
    });
    setEditCADialogOpen(true);
  };

  const handleEditCASubmit = async () => {
    const materialTotal = editCAForm.cost_analysis.material_losses.reduce((sum, m) => sum + (m.total_cost || 0), 0);
    const manpowerTotal = editCAForm.cost_analysis.manpower_losses.reduce((sum, m) => sum + (m.total_cost || 0), 0);
    const payload = {
      ca_completion_notes: editCAForm.completion_notes,
      cost_analysis: {
        ...editCAForm.cost_analysis,
        material_total: materialTotal,
        manpower_total: manpowerTotal,
        total_financial_impact: materialTotal + manpowerTotal + (editCAForm.cost_analysis.other_costs_amount || 0)
      }
    };
    setSubmitting(true);
    try {
      await axios.patch(`${API_URL}/api/complaints/${selectedComplaint.id}/edit-ca-response`, payload, { headers: getAuthHeader() });
      toast.success('CA response updated');
      setEditCADialogOpen(false);
      fetchData();
      const res = await axios.get(`${API_URL}/api/complaints/${selectedComplaint.id}`, { headers: getAuthHeader() });
      setSelectedComplaint(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update CA response');
    } finally {
      setSubmitting(false);
    }
  };

  const addEditCAMaterial = () => {
    setEditCAForm(prev => ({
      ...prev,
      cost_analysis: { ...prev.cost_analysis, material_losses: [...prev.cost_analysis.material_losses, { item_name: '', quantity: 0, unit_cost: 0, total_cost: 0 }] }
    }));
  };
  const addEditCAManpower = () => {
    setEditCAForm(prev => ({
      ...prev,
      cost_analysis: { ...prev.cost_analysis, manpower_losses: [...prev.cost_analysis.manpower_losses, { worker_name: '', hours: 0, hourly_rate: 0, total_cost: 0 }] }
    }));
  };
  const updateEditCAMaterial = (index, field, value) => {
    setEditCAForm(prev => {
      const updated = [...prev.cost_analysis.material_losses];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'quantity' || field === 'unit_cost') updated[index].total_cost = (updated[index].quantity || 0) * (updated[index].unit_cost || 0);
      return { ...prev, cost_analysis: { ...prev.cost_analysis, material_losses: updated } };
    });
  };
  const updateEditCAManpower = (index, field, value) => {
    setEditCAForm(prev => {
      const updated = [...prev.cost_analysis.manpower_losses];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'hours' || field === 'hourly_rate') updated[index].total_cost = (updated[index].hours || 0) * (updated[index].hourly_rate || 0);
      return { ...prev, cost_analysis: { ...prev.cost_analysis, manpower_losses: updated } };
    });
  };

  // ===== EDIT PA RESPONSE =====
  const openEditPADialog = () => {
    const c = selectedComplaint;
    setEditPAForm({
      root_cause_analysis: c.pa_root_cause_analysis || '',
      preventive_action: c.pa_description || '',
      completion_notes: c.pa_completion_notes || ''
    });
    setEditPADialogOpen(true);
  };

  const handleEditPASubmit = async () => {
    if (!editPAForm.root_cause_analysis || !editPAForm.preventive_action) {
      toast.error('RCA and Preventive Action are required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.patch(`${API_URL}/api/complaints/${selectedComplaint.id}/edit-pa-response`, editPAForm, { headers: getAuthHeader() });
      toast.success('PA response updated');
      setEditPADialogOpen(false);
      fetchData();
      const res = await axios.get(`${API_URL}/api/complaints/${selectedComplaint.id}`, { headers: getAuthHeader() });
      setSelectedComplaint(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update PA response');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <AlertOctagon className="w-6 h-6 text-red-600" />
              Complaints Dashboard
            </h1>
            <p className="text-muted-foreground mt-1">
              Track quality issues, corrective & preventive actions
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-1" />
              New Complaint
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">Open</p>
                  <p className="text-2xl font-bold">{stats.open}</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-yellow-200 bg-yellow-50/30">
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-yellow-700">CA/PA Assigned</p>
                  <p className="text-2xl font-bold text-yellow-800">{stats.ca_pending}</p>
                  {stats.ca_overdue > 0 && (
                    <p className="text-xs text-red-600">⚠️ {stats.ca_overdue} overdue</p>
                  )}
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-indigo-200 bg-indigo-50/30">
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-indigo-700">PA Active</p>
                  <p className="text-2xl font-bold text-indigo-800">{stats.pa_pending}</p>
                  {stats.pa_overdue > 0 && (
                    <p className="text-xs text-red-600">⚠️ {stats.pa_overdue} overdue</p>
                  )}
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-green-200 bg-green-50/30">
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-green-700">Closed (MTD)</p>
                  <p className="text-2xl font-bold text-green-800">{stats.closed_this_month}</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-red-200 bg-red-50/30">
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-red-700">💰 Loss (MTD)</p>
                  <p className="text-xl font-bold text-red-800">{formatCurrency(stats.mtd_total_loss)}</p>
                  <p className="text-xs text-muted-foreground">
                    Mat: {formatCurrency(stats.mtd_material_loss)} | Lab: {formatCurrency(stats.mtd_manpower_loss)}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filter Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setStatusFilter(v); }} className="w-full">
          <TabsList className="mb-4 flex-wrap h-auto gap-1">
            <TabsTrigger value="all" className="data-[state=active]:bg-gray-700 data-[state=active]:text-white">
              All ({complaints.length})
            </TabsTrigger>
            <TabsTrigger value="raised" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              Raised ({complaints.filter(c => c.status === 'raised').length})
            </TabsTrigger>
            <TabsTrigger value="ca_assigned" className="data-[state=active]:bg-yellow-600 data-[state=active]:text-white">
              CA/PA Assigned ({complaints.filter(c => ['ca_assigned', 'ca_in_progress'].includes(c.status)).length})
            </TabsTrigger>
            <TabsTrigger value="pa_assigned" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
              PA Active ({complaints.filter(c => ['ca_completed', 'pa_assigned', 'pa_in_progress'].includes(c.status)).length})
            </TabsTrigger>
            <TabsTrigger value="closed" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
              Closed ({complaints.filter(c => c.status === 'closed').length})
            </TabsTrigger>
            <TabsTrigger value="rejected" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
              Rejected ({complaints.filter(c => c.status === 'rejected').length})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Search and Additional Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search complaints..."
              className="pl-9"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="internal">Internal</SelectItem>
              <SelectItem value="sales">Sales</SelectItem>
              <SelectItem value="customer">Customer</SelectItem>
            </SelectContent>
          </Select>
          <Select value={severityFilter} onValueChange={setSeverityFilter}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Severity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Severity</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="major">Major</SelectItem>
              <SelectItem value="minor">Minor</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Complaints Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Complaints Register</CardTitle>
            <CardDescription>{filteredComplaints.length} complaints</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Complaint #</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead className="text-center">Source</TableHead>
                  <TableHead className="text-center">Severity</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right">Cost Impact</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <RefreshCw className="w-5 h-5 animate-spin mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : filteredComplaints.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12">
                      <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-500 opacity-50" />
                      <p className="text-muted-foreground">No complaints found</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredComplaints.map((complaint) => {
                    const SourceIcon = sourceConfig[complaint.source]?.icon || Factory;
                    return (
                      <TableRow key={complaint.id} className="hover:bg-muted/30">
                        <TableCell>
                          <Badge variant="outline" className="font-mono text-xs">
                            {complaint.complaint_no}
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDate(complaint.raised_date)}
                          </p>
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          <p className="font-medium truncate" title={complaint.title}>{complaint.title}</p>
                          <p className="text-xs text-muted-foreground truncate">{complaint.raised_by_name}</p>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="text-xs">
                            <SourceIcon className="w-3 h-3 mr-1" />
                            {sourceConfig[complaint.source]?.label?.split(' ')[0]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className={severityConfig[complaint.severity]?.color}>
                            {severityConfig[complaint.severity]?.icon} {severityConfig[complaint.severity]?.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {complaint.work_order_number && (
                            <Badge variant="secondary" className="text-xs">{complaint.work_order_number}</Badge>
                          )}
                          {complaint.process_name && (
                            <p className="text-xs text-muted-foreground">{complaint.process_name}</p>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={statusConfig[complaint.status]?.color}>
                            {statusConfig[complaint.status]?.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {complaint.cost_analysis?.total_financial_impact > 0 ? (
                            <span className="font-medium text-red-600">
                              {formatCurrency(complaint.cost_analysis.total_financial_impact)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedComplaint(complaint);
                                setDetailDialogOpen(true);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {canEditDelete(complaint) && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-blue-600 hover:text-blue-700"
                                  onClick={() => openEditDialog(complaint)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => {
                                    setSelectedComplaint(complaint);
                                    setDeleteDialogOpen(true);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Create Complaint Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>New Complaint</DialogTitle>
              <DialogDescription>Raise a quality complaint</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Source *</Label>
                  <Select value={newComplaint.source} onValueChange={(v) => setNewComplaint({ ...newComplaint, source: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="internal">Internal (Factory)</SelectItem>
                      <SelectItem value="sales">Sales Team</SelectItem>
                      <SelectItem value="customer">Customer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Severity *</Label>
                  <Select value={newComplaint.severity} onValueChange={(v) => setNewComplaint({ ...newComplaint, severity: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="critical">🔴 Critical</SelectItem>
                      <SelectItem value="major">🟠 Major</SelectItem>
                      <SelectItem value="minor">🟡 Minor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Category *</Label>
                  <Select value={newComplaint.category} onValueChange={(v) => setNewComplaint({ ...newComplaint, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Quality Defect">Quality Defect</SelectItem>
                      <SelectItem value="Damage">Damage</SelectItem>
                      <SelectItem value="Missing Parts">Missing Parts</SelectItem>
                      <SelectItem value="Wrong Item">Wrong Item</SelectItem>
                      <SelectItem value="Delayed Delivery">Delayed Delivery</SelectItem>
                      <SelectItem value="Packaging Issue">Packaging Issue</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Title *</Label>
                <Input
                  value={newComplaint.title}
                  onChange={(e) => setNewComplaint({ ...newComplaint, title: e.target.value })}
                  placeholder="Brief description of the issue"
                  className="uppercase"
                />
              </div>
              
              <div>
                <Label>Description *</Label>
                <Textarea
                  value={newComplaint.description}
                  onChange={(e) => setNewComplaint({ ...newComplaint, description: e.target.value })}
                  placeholder="Detailed description of the complaint..."
                  rows={4}
                  className="uppercase"
                />
              </div>

              {/* Image Upload Section */}
              <div className="border rounded-lg p-4 bg-blue-50/30">
                <Label className="flex items-center gap-2 mb-3">
                  <Image className="w-4 h-4" /> Attach Images (Max 5)
                </Label>
                <div className="flex gap-3 flex-wrap mb-3">
                  {imagePreview.map((preview, idx) => (
                    <div key={idx} className="relative">
                      <img src={preview} alt={`Preview ${idx + 1}`} className="w-20 h-20 object-cover rounded border" />
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {newComplaint.images.length < 5 && (
                    <label className="w-20 h-20 border-2 border-dashed border-gray-300 rounded flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                      <Plus className="w-6 h-6 text-gray-400" />
                      <span className="text-xs text-gray-400">Add</span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handleImageUpload}
                        disabled={uploadingImages}
                      />
                    </label>
                  )}
                </div>
                {uploadingImages && (
                  <div className="flex items-center gap-2 text-sm text-blue-600">
                    <RefreshCw className="w-4 h-4 animate-spin" /> Uploading...
                  </div>
                )}
                <p className="text-xs text-muted-foreground">Supported: JPG, PNG, GIF, WebP (Max 10MB each)</p>
              </div>
              
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-3">References (Optional)</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Work Order</Label>
                    <Select value={newComplaint.work_order_id} onValueChange={(v) => setNewComplaint({ ...newComplaint, work_order_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Select WO" /></SelectTrigger>
                      <SelectContent>
                        {workOrders.slice(0, 50).map(wo => (
                          <SelectItem key={wo.id} value={wo.id}>{wo.work_order_number} - {wo.item_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Process</Label>
                    <Select value={newComplaint.process_name} onValueChange={(v) => setNewComplaint({ ...newComplaint, process_name: v })}>
                      <SelectTrigger><SelectValue placeholder="Select Process" /></SelectTrigger>
                      <SelectContent>
                        {processes.map(p => (
                          <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {newComplaint.source === 'customer' && (
                  <div className="mt-3">
                    <Label>Customer Name</Label>
                    <Input
                      value={newComplaint.customer_name}
                      onChange={(e) => setNewComplaint({ ...newComplaint, customer_name: e.target.value })}
                      placeholder="Customer who raised the complaint"
                      className="uppercase"
                    />
                  </div>
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateComplaint} disabled={submitting}>
                {submitting ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                Submit Complaint
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                {selectedComplaint?.complaint_no}: {selectedComplaint?.title}
              </DialogTitle>
              <DialogDescription>
                Raised by {selectedComplaint?.raised_by_name} on {formatDate(selectedComplaint?.raised_date)}
              </DialogDescription>
            </DialogHeader>
            
            {selectedComplaint && (
              <div className="space-y-6">
                {/* Complaint Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground">Source</p>
                    <p className="font-medium">{sourceConfig[selectedComplaint.source]?.label}</p>
                  </div>
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground">Severity</p>
                    <Badge className={severityConfig[selectedComplaint.severity]?.color}>
                      {severityConfig[selectedComplaint.severity]?.icon} {severityConfig[selectedComplaint.severity]?.label}
                    </Badge>
                  </div>
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground">Category</p>
                    <p className="font-medium">{selectedComplaint.category}</p>
                  </div>
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground">Status</p>
                    <Badge className={statusConfig[selectedComplaint.status]?.color}>
                      {statusConfig[selectedComplaint.status]?.label}
                    </Badge>
                  </div>
                </div>
                
                <div className="bg-muted/30 p-4 rounded-lg">
                  <p className="text-sm font-medium mb-2">Description</p>
                  <p className="text-sm">{selectedComplaint.description}</p>
                </div>

                {/* Images Section */}
                {selectedComplaint.images && selectedComplaint.images.length > 0 && (
                  <div className="border rounded-lg p-4 bg-blue-50/30">
                    <p className="text-sm font-medium mb-3 flex items-center gap-2">
                      <Image className="w-4 h-4" /> Attached Images ({selectedComplaint.images.length})
                    </p>
                    <div className="grid grid-cols-4 gap-3">
                      {selectedComplaint.images.map((img, idx) => (
                        <a 
                          key={idx} 
                          href={img.startsWith('/api') ? `${API_URL}${img}` : img} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          <img 
                            src={img.startsWith('/api') ? `${API_URL}${img}` : img} 
                            alt={`Complaint image ${idx + 1}`} 
                            className="w-full h-24 object-cover rounded border hover:opacity-80 cursor-pointer"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* References */}
                {(selectedComplaint.work_order_number || selectedComplaint.process_name || selectedComplaint.customer_name) && (
                  <div className="flex gap-2 flex-wrap">
                    {selectedComplaint.work_order_number && (
                      <Badge variant="secondary">WO: {selectedComplaint.work_order_number}</Badge>
                    )}
                    {selectedComplaint.process_name && (
                      <Badge variant="secondary">Process: {selectedComplaint.process_name}</Badge>
                    )}
                    {selectedComplaint.customer_name && (
                      <Badge variant="secondary">Customer: {selectedComplaint.customer_name}</Badge>
                    )}
                  </div>
                )}
                
                {/* Corrective Action Section */}
                <Card className="border-yellow-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span>Corrective Action (CA)</span>
                      <div className="flex gap-2">
                        {selectedComplaint.ca_status === 'pending' && selectedComplaint.ca_assigned_to_id === user?.id && (
                          <Button size="sm" variant="outline" onClick={() => setCaCompleteDialogOpen(true)} data-testid="complete-ca-btn">
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Complete CA
                          </Button>
                        )}
                        {selectedComplaint.ca_status === 'completed' && (selectedComplaint.ca_assigned_to_id === user?.id || user?.is_owner || user?.allowed_modules?.includes('complaints_dashboard')) && (
                          <Button size="sm" variant="outline" onClick={openEditCADialog} data-testid="edit-ca-response-btn">
                            <Edit className="w-4 h-4 mr-1" /> Edit Response
                          </Button>
                        )}
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedComplaint.ca_assigned_to_id ? (
                      <div className="space-y-2">
                        {selectedComplaint.ca_description && (
                          <p className="text-sm"><strong>Action:</strong> {selectedComplaint.ca_description}</p>
                        )}
                        <p className="text-sm"><strong>Assigned to:</strong> {selectedComplaint.ca_assigned_to_name}</p>
                        <p className="text-sm"><strong>Target:</strong> {formatDate(selectedComplaint.ca_target_date)}</p>
                        <p className="text-sm"><strong>Status:</strong> <Badge variant="outline">{selectedComplaint.ca_status}</Badge></p>
                        {selectedComplaint.ca_completion_notes && (
                          <p className="text-sm"><strong>Notes:</strong> {selectedComplaint.ca_completion_notes}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Not assigned yet</p>
                    )}
                  </CardContent>
                </Card>
                
                {/* Cost Analysis */}
                {selectedComplaint.cost_analysis && (
                  <Card className="border-red-200 bg-red-50/30">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <IndianRupee className="w-4 h-4" /> Cost Analysis
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-4 gap-4 text-center">
                        <div>
                          <p className="text-xs text-muted-foreground">Material Loss</p>
                          <p className="font-bold text-red-600">{formatCurrency(selectedComplaint.cost_analysis.material_total)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Manpower Loss</p>
                          <p className="font-bold text-red-600">{formatCurrency(selectedComplaint.cost_analysis.manpower_total)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Other Costs</p>
                          <p className="font-bold text-red-600">{formatCurrency(selectedComplaint.cost_analysis.other_costs_amount)}</p>
                        </div>
                        <div className="bg-red-100 rounded p-2">
                          <p className="text-xs text-red-700">Total Impact</p>
                          <p className="font-bold text-red-800 text-lg">{formatCurrency(selectedComplaint.cost_analysis.total_financial_impact)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Preventive Action Section */}
                <Card className="border-indigo-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span>Preventive Action (PA)</span>
                      {selectedComplaint.pa_status === 'completed' && (selectedComplaint.pa_assigned_to_id === user?.id || user?.is_owner || user?.allowed_modules?.includes('complaints_dashboard')) && (
                        <Button size="sm" variant="outline" onClick={openEditPADialog} data-testid="edit-pa-response-btn">
                          <Edit className="w-4 h-4 mr-1" /> Edit Response
                        </Button>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedComplaint.pa_assigned_to_id ? (
                      <div className="space-y-3">
                        {/* Root Cause Analysis - Highlighted */}
                        {selectedComplaint.pa_root_cause_analysis && (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                            <p className="text-xs font-medium text-amber-800 mb-1">Root Cause Analysis</p>
                            <p className="text-sm text-amber-900">{selectedComplaint.pa_root_cause_analysis}</p>
                          </div>
                        )}
                        {selectedComplaint.pa_description && (
                          <p className="text-sm"><strong>Preventive Action:</strong> {selectedComplaint.pa_description}</p>
                        )}
                        <p className="text-sm"><strong>Assigned to:</strong> {selectedComplaint.pa_assigned_to_name}</p>
                        <p className="text-sm"><strong>Target:</strong> {formatDate(selectedComplaint.pa_target_date)}</p>
                        <p className="text-sm"><strong>Status:</strong> <Badge variant="outline">{selectedComplaint.pa_status}</Badge></p>
                        {selectedComplaint.pa_completion_notes && (
                          <p className="text-sm"><strong>Completion Notes:</strong> {selectedComplaint.pa_completion_notes}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Not assigned yet</p>
                    )}
                  </CardContent>
                </Card>

                {/* Assign CA & PA Button - Only show if not already assigned */}
                {!selectedComplaint.ca_assigned_to_id && selectedComplaint.status !== 'closed' && selectedComplaint.status !== 'rejected' && (
                  <div className="pt-2">
                    <Button 
                      className="w-full bg-red-600 hover:bg-red-700" 
                      onClick={() => {
                        setCapaForm({
                          ca_description: '',
                          ca_assigned_to_id: '',
                          ca_assigned_to_name: '',
                          ca_target_date: '',
                          pa_assigned_to_id: '',
                          pa_assigned_to_name: '',
                          pa_target_date: ''
                        });
                        setCapaDialogOpen(true);
                      }}
                    >
                      <UserCheck className="w-4 h-4 mr-2" /> Assign CA & PA
                    </Button>
                  </div>
                )}
                
                {/* History */}
                {selectedComplaint.history?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">History</p>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {selectedComplaint.history.map((h, i) => (
                        <div key={i} className="text-xs bg-muted/30 p-2 rounded">
                          <span className="font-medium">{h.action}</span>
                          <span className="text-muted-foreground"> by {h.by_name} • {formatDate(h.timestamp)}</span>
                          {h.notes && <p className="text-muted-foreground mt-1">{h.notes}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Actions */}
                {selectedComplaint.status !== 'closed' && selectedComplaint.status !== 'rejected' && selectedComplaint.pa_status === 'completed' && (
                  <DialogFooter>
                    <Button onClick={handleCloseComplaint} className="bg-green-600 hover:bg-green-700">
                      <CheckCircle2 className="w-4 h-4 mr-2" /> Close Complaint
                    </Button>
                  </DialogFooter>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Combined CA & PA Assignment Dialog */}
        <Dialog open={capaDialogOpen} onOpenChange={setCapaDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Assign CA & PA</DialogTitle>
              <DialogDescription>
                Assign both Corrective Action and Preventive Action for this complaint
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6">
              {/* CA Section */}
              <div className="border-2 border-yellow-300 rounded-lg p-4 bg-yellow-50/30">
                <h4 className="font-semibold mb-3 text-yellow-800">Corrective Action (CA)</h4>
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm">Action Description (Optional)</Label>
                    <Textarea
                      value={capaForm.ca_description}
                      onChange={(e) => setCapaForm({ ...capaForm, ca_description: e.target.value })}
                      placeholder="Brief description of corrective action..."
                      rows={2}
                      className="bg-white uppercase"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Assign To *</Label>
                      <Select value={capaForm.ca_assigned_to_id} onValueChange={(v) => {
                        const u = users.find(u => u.id === v);
                        setCapaForm({ ...capaForm, ca_assigned_to_id: v, ca_assigned_to_name: u?.name || '' });
                      }}>
                        <SelectTrigger className="bg-white"><SelectValue placeholder="Select person" /></SelectTrigger>
                        <SelectContent>
                          {users.map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.name} ({u.role || 'User'})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">Target Date *</Label>
                      <Input
                        type="date"
                        value={capaForm.ca_target_date}
                        onChange={(e) => setCapaForm({ ...capaForm, ca_target_date: e.target.value })}
                        className="bg-white"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* PA Section */}
              <div className="border-2 border-indigo-300 rounded-lg p-4 bg-indigo-50/30">
                <h4 className="font-semibold mb-3 text-indigo-800">Preventive Action (PA)</h4>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Assign To *</Label>
                      <Select value={capaForm.pa_assigned_to_id} onValueChange={(v) => {
                        const u = users.find(u => u.id === v);
                        setCapaForm({ ...capaForm, pa_assigned_to_id: v, pa_assigned_to_name: u?.name || '' });
                      }}>
                        <SelectTrigger className="bg-white"><SelectValue placeholder="Select person" /></SelectTrigger>
                        <SelectContent>
                          {users.map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.name} ({u.role || 'User'})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">Target Date *</Label>
                      <Input
                        type="date"
                        value={capaForm.pa_target_date}
                        onChange={(e) => setCapaForm({ ...capaForm, pa_target_date: e.target.value })}
                        className="bg-white"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-indigo-600 italic">
                    RCA and PA details will be filled by the assignee when completing the task.
                  </p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCapaDialogOpen(false)}>Cancel</Button>
              <Button 
                className="bg-red-600 hover:bg-red-700"
                onClick={handleAssignCAPA} 
                disabled={submitting || !capaForm.ca_assigned_to_id || !capaForm.ca_target_date || !capaForm.pa_assigned_to_id || !capaForm.pa_target_date}
              >
                {submitting ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                Assign CA & PA
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* CA Complete with Cost Analysis Dialog */}
        <Dialog open={caCompleteDialogOpen} onOpenChange={setCaCompleteDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Complete Corrective Action</DialogTitle>
              <DialogDescription>Record completion details and cost impact</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              <div>
                <Label>Completion Notes *</Label>
                <Textarea
                  value={caCompleteForm.completion_notes}
                  onChange={(e) => setCaCompleteForm({ ...caCompleteForm, completion_notes: e.target.value })}
                  placeholder="Describe what was done to fix the issue..."
                  rows={3}
                />
              </div>
              
              {/* Material Loss */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base">Material Loss</Label>
                  <Button size="sm" variant="outline" onClick={addMaterialLoss}>
                    <Plus className="w-4 h-4 mr-1" /> Add Material
                  </Button>
                </div>
                {caCompleteForm.cost_analysis.material_losses.map((m, i) => (
                  <div key={i} className="grid grid-cols-5 gap-2 mb-2 items-end">
                    <div>
                      <Label className="text-xs">Item</Label>
                      <Input
                        value={m.item_name}
                        onChange={(e) => updateMaterialLoss(i, 'item_name', e.target.value)}
                        placeholder="Item name"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Qty</Label>
                      <Input
                        type="number"
                        value={m.quantity}
                        onChange={(e) => updateMaterialLoss(i, 'quantity', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Unit Cost</Label>
                      <Input
                        type="number"
                        value={m.unit_cost}
                        onChange={(e) => updateMaterialLoss(i, 'unit_cost', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Total</Label>
                      <Input value={formatCurrency(m.total_cost)} disabled className="bg-muted" />
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600"
                      onClick={() => {
                        setCaCompleteForm(prev => ({
                          ...prev,
                          cost_analysis: {
                            ...prev.cost_analysis,
                            material_losses: prev.cost_analysis.material_losses.filter((_, idx) => idx !== i)
                          }
                        }));
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <div className="text-right font-medium mt-2">
                  Material Total: {formatCurrency(caCompleteForm.cost_analysis.material_losses.reduce((sum, m) => sum + m.total_cost, 0))}
                </div>
              </div>
              
              {/* Manpower Loss */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base">Manpower Loss</Label>
                  <Button size="sm" variant="outline" onClick={addManpowerLoss}>
                    <Plus className="w-4 h-4 mr-1" /> Add Worker
                  </Button>
                </div>
                {caCompleteForm.cost_analysis.manpower_losses.map((m, i) => (
                  <div key={i} className="grid grid-cols-5 gap-2 mb-2 items-end">
                    <div>
                      <Label className="text-xs">Worker</Label>
                      <Input
                        value={m.worker_name}
                        onChange={(e) => updateManpowerLoss(i, 'worker_name', e.target.value)}
                        placeholder="Worker name"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Hours</Label>
                      <Input
                        type="number"
                        value={m.hours}
                        onChange={(e) => updateManpowerLoss(i, 'hours', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Rate/Hr</Label>
                      <Input
                        type="number"
                        value={m.hourly_rate}
                        onChange={(e) => updateManpowerLoss(i, 'hourly_rate', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Total</Label>
                      <Input value={formatCurrency(m.total_cost)} disabled className="bg-muted" />
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600"
                      onClick={() => {
                        setCaCompleteForm(prev => ({
                          ...prev,
                          cost_analysis: {
                            ...prev.cost_analysis,
                            manpower_losses: prev.cost_analysis.manpower_losses.filter((_, idx) => idx !== i)
                          }
                        }));
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <div className="text-right font-medium mt-2">
                  Manpower Total: {formatCurrency(caCompleteForm.cost_analysis.manpower_losses.reduce((sum, m) => sum + m.total_cost, 0))}
                </div>
              </div>
              
              {/* Other Costs */}
              <div className="border rounded-lg p-4">
                <Label className="text-base">Other Costs</Label>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <Label className="text-xs">Description</Label>
                    <Input
                      value={caCompleteForm.cost_analysis.other_costs_description}
                      onChange={(e) => setCaCompleteForm(prev => ({
                        ...prev,
                        cost_analysis: { ...prev.cost_analysis, other_costs_description: e.target.value }
                      }))}
                      placeholder="Machine downtime, overhead, etc."
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Amount</Label>
                    <Input
                      type="number"
                      value={caCompleteForm.cost_analysis.other_costs_amount}
                      onChange={(e) => setCaCompleteForm(prev => ({
                        ...prev,
                        cost_analysis: { ...prev.cost_analysis, other_costs_amount: parseFloat(e.target.value) || 0 }
                      }))}
                    />
                  </div>
                </div>
              </div>
              
              {/* Total */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <p className="text-sm text-red-700">Total Financial Impact</p>
                <p className="text-2xl font-bold text-red-800">
                  {formatCurrency(
                    caCompleteForm.cost_analysis.material_losses.reduce((sum, m) => sum + m.total_cost, 0) +
                    caCompleteForm.cost_analysis.manpower_losses.reduce((sum, m) => sum + m.total_cost, 0) +
                    (caCompleteForm.cost_analysis.other_costs_amount || 0)
                  )}
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setCaCompleteDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCompleteCA} disabled={submitting}>
                {submitting ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                Complete CA
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Complaint Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Complaint</DialogTitle>
              <DialogDescription>
                {editComplaint?.complaint_no} - Update complaint details
              </DialogDescription>
            </DialogHeader>
            
            {editComplaint && (
              <div className="space-y-4">
                <div>
                  <Label>Severity</Label>
                  <Select value={editComplaint.severity} onValueChange={(v) => setEditComplaint({ ...editComplaint, severity: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minor">Minor</SelectItem>
                      <SelectItem value="major">Major</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Title *</Label>
                  <Input
                    value={editComplaint.title}
                    onChange={(e) => setEditComplaint({ ...editComplaint, title: e.target.value })}
                    className="uppercase"
                  />
                </div>
                <div>
                  <Label>Description *</Label>
                  <Textarea
                    value={editComplaint.description}
                    onChange={(e) => setEditComplaint({ ...editComplaint, description: e.target.value })}
                    rows={4}
                    className="uppercase"
                  />
                </div>
                {editComplaint.source === 'customer' && (
                  <div>
                    <Label>Customer Name</Label>
                    <Input
                      value={editComplaint.customer_name}
                      onChange={(e) => setEditComplaint({ ...editComplaint, customer_name: e.target.value })}
                      className="uppercase"
                    />
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleEditComplaint} disabled={submitting}>
                {submitting ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600">Delete Complaint</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete complaint {selectedComplaint?.complaint_no}?
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <p className="text-sm text-muted-foreground">
                This action cannot be undone. The complaint "{selectedComplaint?.title}" will be permanently removed.
              </p>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDeleteComplaint} disabled={submitting}>
                {submitting ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit CA Response Dialog */}
        <Dialog open={editCADialogOpen} onOpenChange={setEditCADialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-ca-dialog">
            <DialogHeader>
              <DialogTitle>Edit CA Response</DialogTitle>
              <DialogDescription>Update completion notes and cost analysis for {selectedComplaint?.complaint_no}</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              <div>
                <Label>Completion Notes *</Label>
                <Textarea
                  value={editCAForm.completion_notes}
                  onChange={(e) => setEditCAForm({ ...editCAForm, completion_notes: e.target.value })}
                  placeholder="Describe what was done to fix the issue..."
                  rows={3}
                  data-testid="edit-ca-notes"
                />
              </div>
              
              {/* Material Loss */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base">Material Loss</Label>
                  <Button size="sm" variant="outline" onClick={addEditCAMaterial} data-testid="edit-ca-add-material">
                    <Plus className="w-4 h-4 mr-1" /> Add Material
                  </Button>
                </div>
                {editCAForm.cost_analysis.material_losses.map((m, i) => (
                  <div key={i} className="grid grid-cols-5 gap-2 mb-2 items-end">
                    <div>
                      <Label className="text-xs">Item</Label>
                      <Input value={m.item_name} onChange={(e) => updateEditCAMaterial(i, 'item_name', e.target.value)} placeholder="Item name" />
                    </div>
                    <div>
                      <Label className="text-xs">Qty</Label>
                      <Input type="number" value={m.quantity} onChange={(e) => updateEditCAMaterial(i, 'quantity', parseFloat(e.target.value) || 0)} />
                    </div>
                    <div>
                      <Label className="text-xs">Unit Cost</Label>
                      <Input type="number" value={m.unit_cost} onChange={(e) => updateEditCAMaterial(i, 'unit_cost', parseFloat(e.target.value) || 0)} />
                    </div>
                    <div>
                      <Label className="text-xs">Total</Label>
                      <Input value={formatCurrency(m.total_cost)} disabled className="bg-muted" />
                    </div>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setEditCAForm(prev => ({ ...prev, cost_analysis: { ...prev.cost_analysis, material_losses: prev.cost_analysis.material_losses.filter((_, idx) => idx !== i) } }))}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <div className="text-right font-medium mt-2">
                  Material Total: {formatCurrency(editCAForm.cost_analysis.material_losses.reduce((sum, m) => sum + m.total_cost, 0))}
                </div>
              </div>
              
              {/* Manpower Loss */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base">Manpower Loss</Label>
                  <Button size="sm" variant="outline" onClick={addEditCAManpower} data-testid="edit-ca-add-manpower">
                    <Plus className="w-4 h-4 mr-1" /> Add Worker
                  </Button>
                </div>
                {editCAForm.cost_analysis.manpower_losses.map((m, i) => (
                  <div key={i} className="grid grid-cols-5 gap-2 mb-2 items-end">
                    <div>
                      <Label className="text-xs">Worker</Label>
                      <Input value={m.worker_name} onChange={(e) => updateEditCAManpower(i, 'worker_name', e.target.value)} placeholder="Worker name" />
                    </div>
                    <div>
                      <Label className="text-xs">Hours</Label>
                      <Input type="number" value={m.hours} onChange={(e) => updateEditCAManpower(i, 'hours', parseFloat(e.target.value) || 0)} />
                    </div>
                    <div>
                      <Label className="text-xs">Rate/Hr</Label>
                      <Input type="number" value={m.hourly_rate} onChange={(e) => updateEditCAManpower(i, 'hourly_rate', parseFloat(e.target.value) || 0)} />
                    </div>
                    <div>
                      <Label className="text-xs">Total</Label>
                      <Input value={formatCurrency(m.total_cost)} disabled className="bg-muted" />
                    </div>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setEditCAForm(prev => ({ ...prev, cost_analysis: { ...prev.cost_analysis, manpower_losses: prev.cost_analysis.manpower_losses.filter((_, idx) => idx !== i) } }))}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <div className="text-right font-medium mt-2">
                  Manpower Total: {formatCurrency(editCAForm.cost_analysis.manpower_losses.reduce((sum, m) => sum + m.total_cost, 0))}
                </div>
              </div>
              
              {/* Other Costs */}
              <div className="border rounded-lg p-4">
                <Label className="text-base">Other Costs</Label>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <Label className="text-xs">Description</Label>
                    <Input
                      value={editCAForm.cost_analysis.other_costs_description}
                      onChange={(e) => setEditCAForm(prev => ({ ...prev, cost_analysis: { ...prev.cost_analysis, other_costs_description: e.target.value } }))}
                      placeholder="Machine downtime, overhead, etc."
                      data-testid="edit-ca-other-desc"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Amount</Label>
                    <Input
                      type="number"
                      value={editCAForm.cost_analysis.other_costs_amount}
                      onChange={(e) => setEditCAForm(prev => ({ ...prev, cost_analysis: { ...prev.cost_analysis, other_costs_amount: parseFloat(e.target.value) || 0 } }))}
                      data-testid="edit-ca-other-amount"
                    />
                  </div>
                </div>
              </div>
              
              {/* Total */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <p className="text-sm text-red-700">Total Financial Impact</p>
                <p className="text-2xl font-bold text-red-800" data-testid="edit-ca-total">
                  {formatCurrency(
                    editCAForm.cost_analysis.material_losses.reduce((sum, m) => sum + m.total_cost, 0) +
                    editCAForm.cost_analysis.manpower_losses.reduce((sum, m) => sum + m.total_cost, 0) +
                    (editCAForm.cost_analysis.other_costs_amount || 0)
                  )}
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditCADialogOpen(false)}>Cancel</Button>
              <Button onClick={handleEditCASubmit} disabled={submitting} data-testid="edit-ca-save-btn">
                {submitting ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit PA Response Dialog */}
        <Dialog open={editPADialogOpen} onOpenChange={setEditPADialogOpen}>
          <DialogContent className="max-w-lg" data-testid="edit-pa-dialog">
            <DialogHeader>
              <DialogTitle>Edit PA Response</DialogTitle>
              <DialogDescription>Update RCA and preventive action for {selectedComplaint?.complaint_no}</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label>Root Cause Analysis *</Label>
                <Textarea
                  value={editPAForm.root_cause_analysis}
                  onChange={(e) => setEditPAForm({ ...editPAForm, root_cause_analysis: e.target.value })}
                  placeholder="What caused the issue?"
                  rows={3}
                  className="uppercase"
                  data-testid="edit-pa-rca"
                />
              </div>
              <div>
                <Label>Preventive Action *</Label>
                <Textarea
                  value={editPAForm.preventive_action}
                  onChange={(e) => setEditPAForm({ ...editPAForm, preventive_action: e.target.value })}
                  placeholder="What will prevent recurrence?"
                  rows={3}
                  className="uppercase"
                  data-testid="edit-pa-action"
                />
              </div>
              <div>
                <Label>Completion Notes</Label>
                <Textarea
                  value={editPAForm.completion_notes}
                  onChange={(e) => setEditPAForm({ ...editPAForm, completion_notes: e.target.value })}
                  placeholder="Additional notes..."
                  rows={2}
                  className="uppercase"
                  data-testid="edit-pa-notes"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditPADialogOpen(false)}>Cancel</Button>
              <Button onClick={handleEditPASubmit} disabled={submitting} data-testid="edit-pa-save-btn">
                {submitting ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
